import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const ShareModal = ({ project, onClose }) => {
  const [copied, setCopied] = useState(false);
  const [shareEmail, setShareEmail] = useState('');
  const [emailSent, setEmailSent] = useState(false);

  const shareUrl = `https://giatibuilder.com/projects/${project?.id}`;

  const handleCopyLink = () => {
    navigator.clipboard?.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleEmailShare = () => {
    if (shareEmail) {
      setEmailSent(true);
      setTimeout(() => {
        setEmailSent(false);
        setShareEmail('');
      }, 2000);
    }
  };

  const handleSocialShare = (platform) => {
    const text = `Check out my project: ${project?.name}`;
    const urls = {
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`
    };
    window.open(urls?.[platform], '_blank', 'width=600,height=400');
  };

  return (
    <div className="fixed inset-0 z-[200] bg-background/80 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
      <div className="bg-card rounded-xl border border-border p-6 md:p-8 max-w-lg w-full animate-scale-in">
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-primary flex items-center justify-center">
              <Icon name="Share2" size={24} color="#FFFFFF" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-foreground">Share Project</h3>
              <p className="text-sm text-muted-foreground mt-1 line-clamp-1">
                {project?.name}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-md transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
            aria-label="Close modal"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="space-y-6">
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Share Link
            </label>
            <div className="flex gap-2">
              <Input
                type="text"
                value={shareUrl}
                readOnly
                className="flex-1"
              />
              <Button
                variant={copied ? "success" : "outline"}
                size="default"
                iconName={copied ? "Check" : "Copy"}
                onClick={handleCopyLink}
                className="min-w-[44px]"
              />
            </div>
            {copied && (
              <p className="text-xs text-success mt-2 flex items-center gap-1">
                <Icon name="CheckCircle2" size={14} />
                Link copied to clipboard!
              </p>
            )}
          </div>

          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Share via Email
            </label>
            <div className="flex gap-2">
              <Input
                type="email"
                placeholder="colleague@example.com"
                value={shareEmail}
                onChange={(e) => setShareEmail(e?.target?.value)}
                className="flex-1"
              />
              <Button
                variant={emailSent ? "success" : "default"}
                size="default"
                iconName={emailSent ? "Check" : "Send"}
                onClick={handleEmailShare}
                disabled={!shareEmail}
                className="min-w-[44px]"
              />
            </div>
            {emailSent && (
              <p className="text-xs text-success mt-2 flex items-center gap-1">
                <Icon name="CheckCircle2" size={14} />
                Invitation sent successfully!
              </p>
            )}
          </div>

          <div>
            <label className="text-sm font-medium text-foreground mb-3 block">
              Share on Social Media
            </label>
            <div className="grid grid-cols-3 gap-3">
              <button
                onClick={() => handleSocialShare('twitter')}
                className="flex flex-col items-center gap-2 p-4 bg-muted hover:bg-muted/80 rounded-lg transition-colors min-h-[88px]"
              >
                <Icon name="Twitter" size={24} className="text-foreground" />
                <span className="text-xs font-medium">Twitter</span>
              </button>
              <button
                onClick={() => handleSocialShare('linkedin')}
                className="flex flex-col items-center gap-2 p-4 bg-muted hover:bg-muted/80 rounded-lg transition-colors min-h-[88px]"
              >
                <Icon name="Linkedin" size={24} className="text-foreground" />
                <span className="text-xs font-medium">LinkedIn</span>
              </button>
              <button
                onClick={() => handleSocialShare('facebook')}
                className="flex flex-col items-center gap-2 p-4 bg-muted hover:bg-muted/80 rounded-lg transition-colors min-h-[88px]"
              >
                <Icon name="Facebook" size={24} className="text-foreground" />
                <span className="text-xs font-medium">Facebook</span>
              </button>
            </div>
          </div>

          <div className="pt-4 border-t border-border">
            <div className="flex items-start gap-2 text-sm text-muted-foreground">
              <Icon name="Info" size={16} className="flex-shrink-0 mt-0.5" />
              <p>
                Anyone with this link can view your project details and download the code package.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareModal;